import { buildPayslipSheet, formatMoney, PAYSLIP_EXTRA_KEYS, TAX_MODE_LABELS, type PayslipSheet, type TaxMode } from '@mt/shared';
import { useSearchParams } from 'react-router-dom';
import { Pencil } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Dialog } from '@/components/ui/dialog';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { cn } from '@/lib/utils';
import { useMyTimesheet } from './attendance-api';
import { todayVN } from './attendance-ui';
import { PayslipSheetView } from './PayslipSheet';
import { monthLabel, useMyPayslip, useMyPayslips, type DriverNotes, type InsuranceDetail, type LineItemView } from './payroll-api';

/** Phiếu lương chi tiết của 1 NV (dùng ở bảng lương và "Hồ sơ của tôi"). */
export interface PayslipData {
  employeeCode: string;
  fullName: string;
  departmentName: string | null;
  asiaStaffCode: string | null;
  stdDays: number;
  workDays: number;
  otHours: number;
  unexcusedDays: number;
  contractSalary: number;
  insuranceSalary: number;
  dependants: number;
  taxMode: TaxMode;
  gross: number;
  taxExempt: number;
  insuranceEmployee: number;
  taxableIncome: number;
  pit: number;
  deductions: number;
  net: number;
  insuranceEmployer?: number;
  employerCost?: number;
  items: LineItemView[];
  insurance?: InsuranceDetail | null;
  /** DEC-768: ghi chú từng ngày tiền tài xế */
  driverNotes?: DriverNotes | null;
  /** DEC-950: tờ công chụp lúc tính lương (lịch ngày, P/KL/V, Tăng ca / Về trễ / Hỗ trợ / Qua đêm, phép năm) */
  sheet?: PayslipSheet | null;
}

const Row = ({ label, value, strong, muted }: { label: string; value: string; strong?: boolean; muted?: boolean }) => (
  <div className={cn('flex justify-between gap-3 py-1', strong && 'font-semibold', muted && 'text-xs text-muted-foreground')}>
    <span>{label}</span>
    <span className="tabular-nums">{value}</span>
  </div>
);

export function PayslipBody({ p, showEmployer, wide }: { p: PayslipData; showEmployer?: boolean; wide?: boolean }) {
  const earnings = p.items.filter((i) => i.kind === 'earning');
  const deductions = p.items.filter((i) => i.kind === 'deduction' && i.autoSource !== 'insurance');
  const item = (i: LineItemView) => (
    <div key={i.code} className="flex justify-between gap-3 py-1">
      <span className="min-w-0">
        {i.name}
        {i.qty !== null && i.calc !== 'formula' && (
          <span className="ml-1 text-xs text-muted-foreground">
            ({i.qty.toLocaleString('vi-VN')}
            {i.autoSource === 'sunday' ? ' công Chủ nhật' : i.calc === 'by_hour' ? ' giờ' : ' công'}
            {i.calc === 'by_hour' && i.rate ? ` × ${formatMoney(i.rate)} đ` : ''})
          </span>
        )}
        {i.overrideAmount !== null && (
          <span className="ml-1 inline-flex items-center gap-0.5 text-xs text-warning-foreground" title={i.overrideReason ?? ''}>
            <Pencil className="size-3" />
            sửa tay
          </span>
        )}
      </span>
      <span className="tabular-nums">{formatMoney(i.amount)}</span>
    </div>
  );
  // DEC-768: ghi chú từng ngày dưới khoản tiền tài xế
  const notesOf = (i: LineItemView) =>
    i.autoSource === 'driver_ot'
      ? p.driverNotes?.ot
      : i.autoSource === 'driver_late'
        ? p.driverNotes?.deduct
        : i.autoSource === 'driver_trip'
          ? p.driverNotes?.trip
          : undefined;
  const withNotes = (i: LineItemView) => {
    const notes = notesOf(i);
    if (!notes?.length) return item(i);
    return (
      <div key={i.code}>
        {item(i)}
        <ul className="mb-1 ml-3 list-disc pl-3 text-xs text-muted-foreground" data-testid={`notes-${i.autoSource}`}>
          {notes.map((n, k) => (
            <li key={k}>{n}</li>
          ))}
        </ul>
      </div>
    );
  };
  // Anh Tiến 05/11/2026: quản trị đối chiếu chi tiết Tăng ca · Về trễ · Hỗ trợ · Qua đêm với khoản tiền tài xế trên bảng lương
  const driverPaid = p.items.filter((i) => i.autoSource === 'driver_ot' || i.autoSource === 'driver_trip').reduce((a, i) => a + i.amount, 0);
  const sheetDriver = p.sheet ? PAYSLIP_EXTRA_KEYS.reduce((a, k) => a + (p.sheet!.extras[k].amount ?? 0), 0) : 0;
  const driverMismatch = !!showEmployer && !!p.sheet && (driverPaid > 0 || sheetDriver > 0) && Math.abs(driverPaid - sheetDriver) >= 1;
  const head = (
    <div className="flex flex-wrap items-center gap-2">
      <span className="font-medium">{p.fullName}</span>
      <span className="text-xs text-muted-foreground">{p.employeeCode}</span>
      {p.asiaStaffCode ? <Badge variant="info">{p.asiaStaffCode}</Badge> : <Badge variant="warning">Chưa mã Asia</Badge>}
      {p.departmentName && <span className="text-xs text-muted-foreground">· {p.departmentName}</span>}
    </div>
  );
  const mismatch = driverMismatch && (
    <div className="rounded-md bg-warning-soft px-3 py-2 text-xs text-warning-foreground" data-testid="driver-mismatch">
      Chi tiết tiền tài xế theo ngày cộng ra <b>{formatMoney(sheetDriver)}</b>, khoản Tăng ca tài xế + Công tác phí trên bảng lương là{' '}
      <b>{formatMoney(driverPaid)}</b>. Thường do <b>Duyệt tiền tài xế tháng</b> đã chụp số trước khi duyệt thêm ngày. Vào Chấm công → Tài xế → Mở lại tháng,
      duyệt lại, rồi bấm <b>Tính lại</b> bảng lương.
    </div>
  );
  if (wide && p.sheet) {
    return (
      <div className="space-y-3 text-sm">
        {head}
        {mismatch}
        <div className="grid gap-6 lg:grid-cols-2">
          <PayslipSheetView sheet={p.sheet} items={p.items} />
          <div className="space-y-3">{money()}</div>
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-3 text-sm">
      {head}
      {mismatch}
      {p.sheet && <PayslipSheetView sheet={p.sheet} items={p.items} />}
      {money()}
    </div>
  );
  function money() {
    return (
      <>
        <div className="grid grid-cols-2 gap-x-4 rounded-md border p-2 text-xs sm:grid-cols-4">
          <div>
            <span className="text-muted-foreground">Công</span>{' '}
            <b className="tabular-nums">
              {p.workDays.toLocaleString('vi-VN')}/{p.stdDays.toLocaleString('vi-VN')}
            </b>
          </div>
          <div>
            <span className="text-muted-foreground">Giờ TC</span> <b className="tabular-nums">{p.otHours.toLocaleString('vi-VN')}</b>
          </div>
          <div>
            <span className="text-muted-foreground">Vắng KP</span> <b className="tabular-nums">{p.unexcusedDays.toLocaleString('vi-VN')}</b>
          </div>
          <div>
            <span className="text-muted-foreground">Thuế</span> <b>{TAX_MODE_LABELS[p.taxMode]}</b>
          </div>
        </div>
        <section>
          <h3 className="border-b pb-1 text-xs font-semibold uppercase text-muted-foreground">Thu nhập</h3>
          {earnings.map(withNotes)}
          <Row label="Tổng thu nhập (gross)" value={formatMoney(p.gross)} strong />
        </section>
        <section>
          <h3 className="border-b pb-1 text-xs font-semibold uppercase text-muted-foreground">Khấu trừ</h3>
          <Row label={`BHXH-BHYT-BHTN (lương đóng BH ${formatMoney(p.insuranceSalary)})`} value={formatMoney(p.insuranceEmployee)} />
          <Row label="Thuế TNCN" value={formatMoney(p.pit)} />
          <Row
            label={`Thu nhập tính thuế (miễn thuế ${formatMoney(p.taxExempt)}, ${p.dependants} người phụ thuộc)`}
            value={formatMoney(p.taxableIncome)}
            muted
          />
          {deductions.map(withNotes)}
        </section>
        <div
          className={cn(
            'flex items-center justify-between rounded-md px-3 py-2',
            p.net < 0 ? 'bg-error-soft text-error-foreground' : 'bg-success-soft text-success-foreground',
          )}
        >
          <span className="font-semibold">Thực lĩnh</span>
          <span className="text-lg font-bold tabular-nums">{formatMoney(p.net)}</span>
        </div>
        {showEmployer && p.insurance && (
          <div className="text-xs text-muted-foreground">
            Công ty đóng: BHXH {formatMoney(p.insurance.employer.social)} · BHYT {formatMoney(p.insurance.employer.health)} · BHTN{' '}
            {formatMoney(p.insurance.employer.unemployment)} · KPCĐ {formatMoney(p.insurance.employer.union)} → chi phí DN {formatMoney(p.employerCost ?? 0)}
          </div>
        )}
      </>
    );
  }
}

/** "Lương của tôi": phiếu lương các kỳ đã duyệt (NV chỉ thấy của mình). Link thông báo mở thẳng phiếu (?phieu=id). */
export function MyPayslips() {
  const list = useMyPayslips();
  const [sp, setSp] = useSearchParams();
  const open = Number(sp.get('phieu')) || null;
  const setOpen = (id: number | null) =>
    setSp(
      (cur) => {
        const n = new URLSearchParams(cur);
        if (id) n.set('phieu', String(id));
        else n.delete('phieu');
        return n;
      },
      { replace: true },
    );
  const slip = useMyPayslip(open);
  if (list.isLoading) return <LoadingState rows={2} />;
  if (list.error) return <ErrorState error={list.error} />;
  return (
    <section className="space-y-1" data-testid="my-payslips">
      <h3 className="text-sm font-semibold">Phiếu lương</h3>
      {!list.data?.length ? (
        <p className="text-xs text-muted-foreground">Chưa có phiếu lương (chỉ hiện kỳ đã được GĐ duyệt).</p>
      ) : (
        <ul className="divide-y rounded-md border text-sm">
          {list.data.map((r) => (
            <li key={r.id}>
              <button type="button" className="flex w-full justify-between px-3 py-2 text-left hover:bg-muted" onClick={() => setOpen(r.id)}>
                <span>Tháng {monthLabel(r.month)}</span>
                <span className="font-medium tabular-nums">{formatMoney(r.net)}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {open !== null && (
        <Dialog open onOpenChange={(o) => !o && setOpen(null)} title={slip.data ? `Phiếu lương tháng ${monthLabel(slip.data.month)}` : 'Phiếu lương'}>
          {slip.isLoading ? (
            <LoadingState />
          ) : slip.error ? (
            <ErrorState error={slip.error} />
          ) : slip.data ? (
            slip.data.sheet ? (
              <PayslipBody p={slip.data} />
            ) : (
              <OldPayslip p={slip.data} />
            )
          ) : (
            <EmptyState title="Không có dữ liệu" />
          )}
        </Dialog>
      )}
    </section>
  );
}

/** Kỳ tính trước DEC-950 (chưa chụp tờ công): dựng lịch từ bảng công hiện tại của chính NV (không có tiền tài xế theo ngày, phép năm). */
function OldPayslip({ p }: { p: PayslipData & { month: string } }) {
  const ts = useMyTimesheet(p.month);
  const row = ts.data?.rows[0];
  const sheet =
    ts.data && row ? buildPayslipSheet({ month: p.month, std: p.stdDays, today: todayVN(), days: ts.data.days, cells: row.cells, totals: row.totals }) : null;
  return (
    <div className="space-y-3">
      {sheet && <PayslipSheetView sheet={sheet} items={p.items} live />}
      <PayslipBody p={p} />
    </div>
  );
}
